/**
 * Capacity task service — OutpostForge frontend (CES standard)
 * Dry-run/apply, task monitoring, cancellation, and blocking instances.
 */

import type {
  CapacityTaskResponse,
  ListBlockingInstancesResponse,
  ListCapacityTasksResponse,
  StartCapacityTaskRequest,
} from '../../types/outposts';
import { http } from '../http';

export const capacityTaskService = {
  async startCapacityTask(
    outpostId: string,
    requestBody: StartCapacityTaskRequest,
  ): Promise<CapacityTaskResponse> {
    const response = await http.post<CapacityTaskResponse>(
      `/outposts/${encodeURIComponent(outpostId)}/capacity-tasks`,
      requestBody,
    );
    return response.data;
  },

  async listCapacityTasks(filters?: {
    outpostId?: string;
    statuses?: readonly string[];
  }): Promise<ListCapacityTasksResponse> {
    const params = new URLSearchParams();
    if (filters?.outpostId) params.set('outpostId', filters.outpostId);
    if (filters?.statuses && filters.statuses.length > 0) {
      params.set('status', filters.statuses.join(','));
    }
    const query = params.toString();
    const response = await http.get<ListCapacityTasksResponse>(
      `/capacity-tasks${query ? `?${query}` : ''}`,
    );
    return response.data;
  },

  async getCapacityTask(outpostId: string, taskId: string): Promise<CapacityTaskResponse> {
    const response = await http.get<CapacityTaskResponse>(
      `/outposts/${encodeURIComponent(outpostId)}/capacity-tasks/${encodeURIComponent(taskId)}`,
    );
    return response.data;
  },

  async cancelCapacityTask(outpostId: string, taskId: string): Promise<unknown> {
    const response = await http.post(
      `/outposts/${encodeURIComponent(outpostId)}/capacity-tasks/${encodeURIComponent(taskId)}/cancel`,
      {},
    );
    return response.data;
  },

  async getBlockingInstances(
    outpostId: string,
    taskId: string,
  ): Promise<ListBlockingInstancesResponse> {
    const response = await http.get<ListBlockingInstancesResponse>(
      `/outposts/${encodeURIComponent(outpostId)}/capacity-tasks/${encodeURIComponent(taskId)}/blocking-instances`,
    );
    return response.data;
  },
};

export type CapacityTaskService = typeof capacityTaskService;
